import React, { useEffect, createContext, useRef } from 'react'
import PropTypes from 'prop-types'
import { analyticsFormattedMessage } from 'Utility/AnalyticsFormattedMessage'
import { nullSafe } from 'Utility/globalMethods'

export const AnalyticsContext = createContext()
export const AnalyticsProvider = ({ children }) => {
  const pendingCtaTargetUrl = useRef(null)
  const analyticsFunctions = {}
  const setPendingCtaTargetUrl = (targetUrl) => {
    pendingCtaTargetUrl.current = targetUrl
  }

  analyticsFunctions.setPendingCtaTargetUrl = setPendingCtaTargetUrl

  if (process.env.GTM_ID) {
    const {
      setCtaAnalytics,
      setMenuAnalytics,
      setTabSwitchingAnalytics,
      setPopupClickAnalytics,
      setInputFieldFocusAnalytics,
      setFormInteraction,
      setOutcomeAnalytics,
      setCustomErrorAnalytics,
      setFaqAnalytics,
      setTooltipAnalytics,
      setModuleAnalytics,
      setDownloadAnalytics,
      setAccordionAnalytics,
      setAssistanceBoxClickAnalytics,
      setToggleChangeAnalytics
    } = require('Store/middleware/analytics')
    analyticsFunctions.setCtaAnalytics = setCtaAnalytics
    analyticsFunctions.setMenuAnalytics = setMenuAnalytics
    analyticsFunctions.setTabSwitchingAnalytics = setTabSwitchingAnalytics
    analyticsFunctions.setPopupClickAnalytics = setPopupClickAnalytics
    analyticsFunctions.setInputFieldFocusAnalytics = setInputFieldFocusAnalytics
    analyticsFunctions.setFormInteraction = setFormInteraction
    analyticsFunctions.setOutcomeAnalytics = setOutcomeAnalytics
    analyticsFunctions.setCustomErrorAnalytics = setCustomErrorAnalytics
    analyticsFunctions.setFaqAnalytics = setFaqAnalytics
    analyticsFunctions.setTooltipAnalytics = setTooltipAnalytics
    analyticsFunctions.setAccordionAnalytics = setAccordionAnalytics
    analyticsFunctions.setModuleAnalytics = setModuleAnalytics
    analyticsFunctions.setDownloadAnalytics = setDownloadAnalytics
    analyticsFunctions.setAssistanceBoxClickAnalytics = setAssistanceBoxClickAnalytics
    analyticsFunctions.setToggleChangeAnalytics = setToggleChangeAnalytics

    const handleCtaAnalytics = (event) => {
      const data = event.detail
      const targetUrl = data.targetUrl ?? data.target_url ?? pendingCtaTargetUrl.current ?? null
      pendingCtaTargetUrl.current = null

      analyticsFunctions.setCtaAnalytics(
        typeof data.label === 'object' ? analyticsFormattedMessage(data.label.props) : data.label,
        data.ctaClass,
        targetUrl,
        data.id
      )
    }

    const handleTooltipAnalytics = (event) => {
      const data = event.detail
      analyticsFunctions.setTooltipAnalytics(
        typeof data.message === 'object'
          ? analyticsFormattedMessage(nullSafe(() => data.message.props, data.message || null))
          : data.message,
        typeof data.title === 'object'
          ? analyticsFormattedMessage(nullSafe(() => data.title.props, data.title || null))
          : data.title
      )
    }

    const handleAccordionAnalytics = (event) => {
      const data = event.detail
      analyticsFunctions.setAccordionAnalytics(
        typeof data.action === 'object'
          ? analyticsFormattedMessage(nullSafe(() => data.message.props, data.message || null))
          : data.action,
        typeof data.title === 'object'
          ? analyticsFormattedMessage(nullSafe(() => data.title.props, data.title || null))
          : data.title
      )
    }

    const handleFormInteractionAnalytics = (event) => {
      const data = event.detail
      analyticsFunctions.setFormInteraction(
        data.action,
        data.id,
        data.element,
        data.result,
        data.message
      )
    }

    /**
     * Retrieves the target element from the given element.
     *
     * @param {HTMLElement} element - The element to retrieve the target element from.
     * @returns {HTMLElement} - The target element.
     */
    const getElement = (element) => {
      let targetElement = element
      if (element.childNodes && element.childNodes.length > 0) {
        const findAnElementWithId = Array.from(element.childNodes).find((element) => element.id)
        if (findAnElementWithId) {
          targetElement = findAnElementWithId
        }
      }

      return targetElement
    }

    const handleInAndOutAnalytics = (event) => {
      const data = event.detail

      const dataLabel = nullSafe(
        () => data.label?.props?.children?.[0]?.props,
        data.label?.props || data.label || null
      )

      let label = ''
      let id = nullSafe(() => data.id || data?.event?.target?.id || null)

      // Caso ad hoc per "selectAll"
      if (id === 'selectAll') {
        const rawText = analyticsFormattedMessage(data.label)?.trim()
        const numMatch = rawText.match(/\d+/)

        if (numMatch) {
          label = `Seleziona tutto ${numMatch[0]} selezionati`
        } else {
          label = rawText
        }
      } else {
        if (React.isValidElement(data.label)) {
          const legacyLabel = dataLabel?.name || dataLabel?.label || ''
          const labelChildren = nullSafe(() => data.label.props.children, null)

          if (legacyLabel) {
            label = legacyLabel
          } else if (typeof labelChildren === 'string') {
            label = labelChildren
          } else if (
            Array.isArray(labelChildren) &&
            labelChildren.every((child) => typeof child === 'string')
          ) {
            label = labelChildren.join('')
          } else {
            label = ''
          }
        } else {
          label = typeof data.label === 'object' ? analyticsFormattedMessage(dataLabel) : data.label
        }
      }

      let className = nullSafe(() => data.class || null)
      let action = nullSafe(() => data.action || null)

      if (nullSafe(() => data.event, null)) {
        // override se disponibili nell'evento
        id = data.event.target.id
        className = data.event.target.className
        action = data.event.type === 'focus' ? 'focusin' : 'focusout'
      }

      analyticsFunctions.setInputFieldFocusAnalytics(label, id, className, action)

      // errori personalizzati per blur
      const currentElement = data?.event?.target
      if (currentElement) {
        const targetElement = getElement(currentElement)
        setTimeout(() => {
          const invalidMessage = targetElement.getAttribute('aria-invalid')
          if (action === 'focusout' && invalidMessage !== 'false' && invalidMessage !== null) {
            setCustomErrorAnalytics?.('input', null, invalidMessage, targetElement.getAttribute('id'))
          }
        }, 0)
      }
    }

    const handleModalAnalytics = (event) => {
      const data = event.detail
      analyticsFunctions.setPopupClickAnalytics(
        data.modalTitle,
        data.modalDescription,
        data.modalPrimaryButtonLabel,
        data.modalSecondaryButtonLabel,
        data.modalCloseButtonLabel,
        data.clicked
      )
    }

    // Handles tab switching analytics events and prevents duplicate tracking.
    // Listens to 'tabSwitching' custom events and sends tab change analytics data to GTM.
    // Implements deduplication logic to filter out redundant events dispatched by the PortalTabs component,
    // which fires the event twice per user action.
    const lastTabSwitchRef = useRef({ label: null, time: 0 })
    const handleTabSwitching = (event) => {
      const data = event.detail
      const label = typeof data.label === 'object' ? analyticsFormattedMessage(data.label.props) : data.label
      const now = Date.now()

      // Filter out duplicate events: if the same label is received within 100ms, ignore it
      if (label === lastTabSwitchRef.current.label && now - lastTabSwitchRef.current.time < 100) {
        return
      }
      lastTabSwitchRef.current = { label, time: now }
      analyticsFunctions.setTabSwitchingAnalytics(label)
    }

    const handleMenuAnalytics = (event) => {
      const data = event.detail
      analyticsFunctions.setMenuAnalytics(
        typeof data.label === 'object' ? analyticsFormattedMessage(data.label.props) : data.label,
        data.target_url,
        data.level
      )
    }

    useEffect(() => {
      if (!process.env.GTM_ID) {
        return
      }
      window.addEventListener('ctaAnalytics', handleCtaAnalytics)
      window.addEventListener('tooltipAnalytics', handleTooltipAnalytics)
      window.addEventListener('accordionAnalytics', handleAccordionAnalytics)
      window.addEventListener('formInteraction', handleFormInteractionAnalytics)
      window.addEventListener('focusInAndOutAnalytics', handleInAndOutAnalytics)
      window.addEventListener('modalAnalytics', handleModalAnalytics)
      window.addEventListener('tabSwitching', handleTabSwitching)
      window.addEventListener('menuAnalytics', handleMenuAnalytics)
      return () => {
        window.removeEventListener('ctaAnalytics', handleCtaAnalytics)
        window.removeEventListener('tooltipAnalytics', handleTooltipAnalytics)
        window.removeEventListener('accordionAnalytics', handleAccordionAnalytics)
        window.removeEventListener('formInteraction', handleFormInteractionAnalytics)
        window.removeEventListener('focusInAndOutAnalytics', handleInAndOutAnalytics)
        window.removeEventListener('modalAnalytics', handleModalAnalytics)
        window.removeEventListener('tabSwitching', handleTabSwitching)
        window.removeEventListener('menuAnalytics', handleMenuAnalytics)
      }
    }, [])
  }
  return <AnalyticsContext.Provider value={analyticsFunctions}>{children}</AnalyticsContext.Provider>
}

AnalyticsProvider.propTypes = {
  children: PropTypes.node.isRequired
}
